import { useEffect, useRef, useState } from "react";
import { useSession } from "@otto/plattform";
import {
  useCourseHasResults,
  useUpdateToLatestVersion,
  type ClassCourse,
} from "../lib/data.js";
import own from "./courses.module.css";
import styles from "../../../shared/components/shell.module.css";

const LABEL = "Neuere Fassung verfügbar";

/**
 * Das Warnzeichen neben der Fassungsnummer – und was dahinter steckt.
 *
 * Kein Tooltip: der Hinweis hat zwei Fälle und im besseren davon einen Knopf.
 * Beides passt nicht in einen `title`, und ein Knopf, den man nur sieht, solange
 * die Maus stillsteht, ist keiner.
 *
 * Die zwei Fälle sind die ganze Aussage:
 *
 *   noch nichts geübt → die Klasse kann nachziehen, hier und jetzt;
 *   schon geübt       → die Fassung steht fest, und das bleibt auch so.
 *
 * Der zweite ist kein Fehler und keine offene Aufgabe. Er ist der Normalfall
 * eines laufenden Lehrgangs: Übungstexte mitten im Lehrgang auszutauschen wäre
 * genau das, was diese Regel verhindert. Die neuere Fassung gilt dann für neu
 * angelegte Klassen – nicht rückwirkend für diese.
 */
export function NewerVersionNotice({
  classId,
  course,
  latestVersion,
  latestVersionId,
}: {
  classId: string;
  course: ClassCourse;
  latestVersion: number;
  latestVersionId: string | null;
}) {
  const { client } = useSession();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLSpanElement>(null);
  // Gefragt wird erst, wenn jemand hinsieht. Eine Klasse mit drei Lehrgängen
  // stellt sonst drei Zählabfragen für einen Hinweis, den niemand aufklappt.
  const results = useCourseHasResults(client, classId, course, open);
  const update = useUpdateToLatestVersion(client, classId);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    const onClick = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  const frozen = results.data === true;
  const canUpdate = results.data === false && latestVersionId !== null;

  return (
    <span className={own.notice} ref={box}>
      <button
        type="button"
        className={own.warn}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={LABEL}
        title={LABEL}
        // Der Knopf sitzt im Kopf des Aufklappers. Ohne das hier klappt jeder
        // Klick auf das Zeichen den ganzen Lehrgang auf oder zu.
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen((before) => !before);
        }}
      >
        <svg
          viewBox="0 0 20 18"
          width="16"
          height="15"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M10 1.5 18.7 16.5H1.3z"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
          <path
            d="M10 7v4.4"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <circle cx="10" cy="13.6" r="1.1" fill="currentColor" />
        </svg>
      </button>

      {open && (
        <span className={own.popover} role="dialog" aria-label={LABEL}>
          <span className={own.popoverTitle}>{LABEL}</span>

          <span className={own.popoverText}>
            Fassung {latestVersion} ist veröffentlicht, diese Klasse übt Fassung{" "}
            {course.version}. Neu angelegte Klassen bekommen die neuere automatisch.
          </span>

          {results.isPending && <span className={own.popoverText}>Wird geprüft …</span>}

          {frozen && (
            <span className={own.popoverText}>
              In dieser Klasse wurde schon geübt – damit steht ihre Fassung fest. Ein
              Wechsel würde die Übungstexte mitten im Lehrgang austauschen, während die
              Kinder mit dem Buch danebensitzen.
            </span>
          )}

          {results.isError && (
            <span className={own.popoverText}>
              Ob in dieser Klasse schon geübt wurde, ließ sich gerade nicht feststellen.
            </span>
          )}

          {canUpdate && (
            <>
              <span className={own.popoverText}>
                Hier hat noch niemand geübt – die Klasse kann die neuere Fassung
                übernehmen.
              </span>
              <button
                type="button"
                className={styles.button}
                disabled={update.isPending}
                onClick={() =>
                  update.mutate(
                    { curriculumId: course.curriculumId, versionId: latestVersionId },
                    { onSuccess: () => setOpen(false) },
                  )
                }
              >
                {update.isPending
                  ? "Wird übernommen …"
                  : `Auf Fassung ${latestVersion} updaten`}
              </button>
            </>
          )}

          {update.isError && (
            <span className={`${styles.notice} ${styles.noticeError}`}>
              Das hat nicht geklappt. Möglicherweise hat inzwischen ein Kind geübt – dann
              steht die Fassung ab jetzt fest.
            </span>
          )}
        </span>
      )}
    </span>
  );
}
